import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, TextField, Typography, Button, Paper } from '@mui/material';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface SimpleCaptchaProps {
  onChange: (isValid: boolean) => void;
  isValid: boolean;
}

/**
 * Eine einfache Captcha-Komponente
 * @param {Function} onChange - Callback-Funktion, die aufgerufen wird, wenn der Captcha-Status sich ändert
 * @param {boolean} isValid - Externer Status, ob das Captcha korrekt gelöst wurde
 */
const SimpleCaptcha: React.FC<SimpleCaptchaProps> = ({ onChange }) => {
  const { t } = useTranslation();
  const [captchaText, setCaptchaText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [localValid, setLocalValid] = useState(false);

  // Generiert einen zufälligen Captcha-Text
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaText(result);
    setUserInput('');
    setLocalValid(false);
    if (onChange) {
      onChange(false);
    }
  };

  // Prüft, ob die Eingabe des Benutzers mit dem Captcha übereinstimmt
  const validateCaptcha = (input: string) => {
    const isMatch = input === captchaText;
    setLocalValid(isMatch);
    if (onChange) {
      onChange(isMatch);
    }
    return isMatch;
  };

  // Initialisiert das Captcha beim Laden der Komponente
  useEffect(() => {
    generateCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {t('captcha.title')}
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Paper
          elevation={3}
          sx={{
            p: 2,
            mr: 2,
            backgroundColor: '#f5f5f5',
            fontFamily: 'monospace',
            fontSize: '1.2rem',
            letterSpacing: '0.1em',
            position: 'relative',
            textAlign: 'center',
            minWidth: 150
          }}
        >
          {captchaText}
        </Paper>
        
        <Button 
          onClick={generateCaptcha}
          startIcon={<RefreshIcon />}
          size="small"
        >
          {t('captcha.refresh')}
        </Button>
      </Box>
      
      <TextField
        fullWidth
        label={t('captcha.inputLabel')}
        value={userInput}
        onChange={(e) => {
          setUserInput(e.target.value);
          validateCaptcha(e.target.value);
        }}
        error={userInput.length > 0 && !localValid}
        helperText={userInput.length > 0 && !localValid 
          ? t('captcha.errorMismatch') 
          : t('captcha.helperText')}
        required
      />
    </Box>
  );
};

export default SimpleCaptcha;
